<div class="provision-view-container">

  @if (loading) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading provision...</p>
    </div>
  }

  @if (error) {
    <mat-card class="error-card">
      <mat-card-content>
        <mat-icon color="warn">error</mat-icon>
        <h2>{{ error }}</h2>
        <button mat-raised-button color="primary" (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Return to Provisions
        </button>
      </mat-card-content>
    </mat-card>
  }

  @if (!loading && !error && currentProvision) {

    <mat-sidenav-container class="sidenav-container">

      <!-- Actions Sidenav (Left Side) -->
      <mat-sidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          title="Provision Actions"
          [actions]="sidenavActions"
          [tabs]="sidenavTabs"
          [selectedTabIndex]="selectedTabIndex"
          (tabSelected)="selectTab($event)">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <!-- Main Content -->
      <mat-sidenav-content class="form-content">

        <!-- Header Actions Bar -->
        <div class="header-bar">
          <button mat-button (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            Back to Provisions
          </button>

          <div class="header-actions">
            <button mat-raised-button color="primary" (click)="editProvision()">
              <mat-icon>edit</mat-icon>
              Edit Provision
            </button>
          </div>
        </div>

        <!-- Static Information Section (Always Visible) -->
        <div class="static-info-section">
          <!-- Provision Name Header -->
          <div class="provision-name-header">
            <h1>{{ currentProvision.provName || 'Unnamed Provision' }}</h1>
            <div class="provision-metadata">
              <span class="provision-num">Provision ID: {{ currentProvision.provNum || '' }}</span>
              @if (currentProvision.approvedProvision) {
                <mat-chip class="approved-chip">
                  <mat-icon>verified</mat-icon>
                  Approved
                </mat-chip>
              }
              @if (currentProvision.actionCompleted) {
                <mat-chip class="completed-chip">
                  <mat-icon>check_circle</mat-icon>
                  Action Completed
                </mat-chip>
              }
            </div>
          </div>

          <!-- Approved Risk Provision stamp (Playbook eligibility) -->
          @if (currentProvision.approvedProvision) {
            @if (currentProvision.actionCompleted) {
              <div class="risk-provision-stamp official">
                <mat-icon>verified</mat-icon>
                <div class="stamp-text">
                  <span class="stamp-title">Official Approved Risk Provision</span>
                  <span class="stamp-sub">
                    Published to the Playbook for:
                    {{ (currentProvision.contractTypes && currentProvision.contractTypes.length) ? currentProvision.contractTypes.join(', ') : 'no contract types selected' }}
                  </span>
                </div>
              </div>
            } @else {
              <div class="risk-provision-stamp pending">
                <mat-icon>hourglass_empty</mat-icon>
                <div class="stamp-text">
                  <span class="stamp-title">Pending Approved Risk Provision</span>
                  <span class="stamp-sub">Mark <strong>Action Completed</strong> to publish this to the Playbook.</span>
                </div>
              </div>
            }
          }

          <!-- Essential Information -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>description</mat-icon>
              <h3>Provision Information</h3>
            </div>
            <div class="info-grid essential-grid">
              <div class="info-field">
                <label>Provision Name</label>
                <div class="value">{{ currentProvision.provName || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Owner</label>
                <div class="value">{{ ownerName }}</div>
              </div>

              <div class="info-field">
                <label>Approver</label>
                <div class="value">{{ approverName }}</div>
              </div>

              <div class="info-field">
                <label>Contract Types</label>
                <div class="value">
                  @if (currentProvision.contractTypes && currentProvision.contractTypes.length > 0) {
                    {{ currentProvision.contractTypes.join(', ') }}
                  } @else {
                    N/A
                  }
                </div>
              </div>

              <div class="info-field">
                <label>Risk Category</label>
                <div class="value">{{ currentProvision.riskCategory || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Critical Date</label>
                <div class="value">{{ formatDate(currentProvision.criticalDate) }}</div>
              </div>

              <div class="info-field">
                <label>Action Date</label>
                <div class="value">{{ formatDate(currentProvision.actionDate) }}</div>
              </div>

              <div class="info-field">
                <label>Action Summary</label>
                <div class="value">{{ currentProvision.actionSummary || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Action Completed</label>
                <div class="value">{{ currentProvision.actionCompleted ? 'Yes' : 'No' }}</div>
              </div>

              <div class="info-field">
                <label>Approved Provision</label>
                <div class="value">{{ currentProvision.approvedProvision ? 'Yes' : 'No' }}</div>
              </div>
            </div>

            <!-- Required Action - Full Width Multi-line -->
            <div class="required-action-section">
              <label>Required Action</label>
              <div class="value multi-line">{{ currentProvision.requiredAction || 'N/A' }}</div>
            </div>
          </div>

          <!-- Source Information -->
          <div class="info-section">
            <div class="section-header">
              <mat-icon>link</mat-icon>
              <h3>Source Information</h3>
            </div>
            <div class="info-grid source-grid">
              <div class="info-field">
                <label>Source Type</label>
                <div class="value">{{ currentProvision.sourceType || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Source Name</label>
                <div class="value">{{ currentProvision.contractFriendlyName || currentProvision.sourceName || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Source ID</label>
                <div class="value">{{ currentProvision.sourceId || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Contract Number</label>
                <div class="value">{{ currentProvision.contractNum || 'N/A' }}</div>
              </div>

              <div class="info-field">
                <label>Link to Source</label>
                <div class="value">
                  @if (currentProvision.linkToSource) {
                    <a [href]="currentProvision.linkToSource" target="_blank" rel="noopener noreferrer" class="source-link">
                      <mat-icon>open_in_new</mat-icon>
                      View Source Document
                    </a>
                  } @else {
                    N/A
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabbed Content -->
        <mat-tab-group
          [selectedIndex]="selectedTabIndex"
          (selectedIndexChange)="selectTab($event)"
          class="provision-tabs">

          <!-- Tab 0: Details -->
          <mat-tab label="Details">
            <div class="tab-content">
              <mat-card class="info-card">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon>notes</mat-icon>
                    Provision Language / Description
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  @if (currentProvision.description) {
                    <div class="provision-description" [innerHTML]="safeDescription"></div>
                  } @else {
                    <p class="no-content">No provision language or description provided.</p>
                  }
                </mat-card-content>
              </mat-card>

              @if (currentProvision.keyPhrases && currentProvision.keyPhrases.length > 0) {
                <mat-card class="info-card">
                  <mat-card-header>
                    <mat-card-title>
                      <mat-icon>label</mat-icon>
                      Key Phrases
                    </mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    <div class="key-phrases-display">
                      @for (phrase of currentProvision.keyPhrases; track phrase) {
                        <mat-chip>{{ phrase }}</mat-chip>
                      }
                    </div>
                  </mat-card-content>
                </mat-card>
              }

              @if (currentProvision.commentsRecommendations) {
                <mat-card class="info-card">
                  <mat-card-header>
                    <mat-card-title>
                      <mat-icon>comment</mat-icon>
                      Comments / Recommendations
                    </mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    <div class="comments-content" [innerHTML]="safeCommentsRecommendations"></div>
                  </mat-card-content>
                </mat-card>
              }

              @if (currentProvision.memo) {
                <mat-card class="info-card">
                  <mat-card-header>
                    <mat-card-title>
                      <mat-icon>note</mat-icon>
                      Memo
                    </mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    <div class="memo-content">{{ currentProvision.memo }}</div>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

          <!-- Tab 1: Risk -->
          <mat-tab label="Risk">
            <div class="tab-content">
              @if (currentProvision.approvedProvision) {
                <mat-card class="approved-card">
                  <mat-card-content>
                    <div class="approved-notice">
                      <mat-icon>verified</mat-icon>
                      <h2>Approved Provision</h2>
                      <p>This is an approved provision - risk scoring is not required.</p>
                    </div>
                  </mat-card-content>
                </mat-card>
              } @else {
                <mat-card class="info-card risk-card">
                  <mat-card-header>
                    <mat-card-title>
                      <mat-icon>assessment</mat-icon>
                      Risk Assessment
                    </mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    <div class="info-grid risk-grid">
                      <div class="info-field">
                        <label>Risk Domain</label>
                        <div class="value">{{ currentProvision.riskDomain || 'N/A' }}</div>
                      </div>

                      <div class="info-field">
                        <label>Risk Name</label>
                        <div class="value">{{ currentProvision.riskName || 'N/A' }}</div>
                      </div>

                      <div class="info-field score-field">
                        <label>Likelihood Score</label>
                        <div class="score-value">
                          <span class="score-number">{{ currentProvision.likelihoodScore || '0' }}</span>
                          <span class="score-label">{{ getRiskScoreLabel(currentProvision.likelihoodScore) }}</span>
                        </div>
                      </div>

                      <div class="info-field score-field">
                        <label>Impact Score</label>
                        <div class="score-value">
                          <span class="score-number">{{ currentProvision.impactScore || '0' }}</span>
                          <span class="score-label">{{ getRiskScoreLabel(currentProvision.impactScore) }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="risk-scale-image">
                      <img src="assets/img/guides/risk_scale.jpg" alt="Risk Scale Guide" class="risk-guide">
                    </div>
                  </mat-card-content>
                </mat-card>
              }
            </div>
          </mat-tab>

        </mat-tab-group>

      </mat-sidenav-content>

    </mat-sidenav-container>

  }

</div>
